import { useEffect, useState } from 'react';
import { FEEDBACK_BUTTONS, LANG_DIR, UI, fill, type FeedbackEntry, type FeedbackPage, type FeedbackQuery, type FeedbackRating, type FeedbackStatus, type Lang } from '@ringring/core';
import { api, errorText, type ApiError } from './api';
import { Icon } from './Icon';
import { Filled } from './lang';
import { ReviewQr } from './QrCode';

const DEFAULT_QUERY: FeedbackQuery = { status: 'all', period: 'all', rating: null, search: '', page: 1 };
const LOCALES: Record<Lang, string> = { fr: 'fr-MA', en: 'en-GB', ar: 'ar-MA' };
const RATINGS = [3, 2, 1] as const;

function ratingLabel(lang: Lang, rating: FeedbackRating): string {
  return FEEDBACK_BUTTONS[lang][3 - rating].title;
}

export interface FeedbackViewProps {
  lang: Lang;
  reviewUrl?: string | null;
  data: FeedbackPage | null;
  query: FeedbackQuery;
  loading: boolean;
  error: string | null;
  reading: string | null;
  onQuery: (patch: Partial<FeedbackQuery>) => void;
  onReset: () => void;
  onRefresh: () => void;
  onRead: (entry: FeedbackEntry) => void;
}

export function FeedbackView({ lang, reviewUrl = null, data, query, loading, error, reading, onQuery, onReset, onRefresh, onRead }: FeedbackViewProps) {
  const t = UI[lang];
  const summary = data?.summary;
  const rated = summary ? summary.excellent + summary.good + summary.not_great : 0;
  const counts = { 3: summary?.excellent ?? 0, 2: summary?.good ?? 0, 1: summary?.not_great ?? 0 };
  const statuses: { value: FeedbackStatus; label: string; count: number | undefined }[] = [
    { value: 'all', label: t.feedback_all, count: summary?.received },
    { value: 'unread', label: t.feedback_unread, count: summary?.unread },
    { value: 'notes', label: t.feedback_private_notes, count: summary?.notes },
    { value: 'pending', label: t.feedback_pending, count: summary?.pending },
  ];
  const filtered = query.status !== 'all' || query.rating !== null || query.search !== '' || query.period !== 'all';
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const date = (ts: number) => new Intl.DateTimeFormat(LOCALES[lang], { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Casablanca' }).format(ts);
  return (
    <section className="feedback-page" lang={lang} dir={LANG_DIR[lang]} aria-labelledby="feedback-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">{t.channel_whatsapp}</p>
          <h1 id="feedback-title" className="display">{t.feedback_heading}</h1>
          <p className="muted">{t.feedback_intro}</p>
        </div>
        <button className="btn secondary" type="button" onClick={onRefresh} disabled={loading}>
          <Icon name="refresh" />{t.feedback_refresh}
        </button>
      </div>

      {reviewUrl ? <ReviewQr lang={lang} url={reviewUrl} /> : null}

      <div className="feedback-filters">
        <label className="feedback-search" htmlFor="feedback-search">
          <span className="sr-only">{t.feedback_search}</span>
          <Icon name="search" />
          <input id="feedback-search" type="search" value={query.search} maxLength={120} placeholder={t.feedback_search} onChange={(e) => onQuery({ search: e.target.value, page: 1 })} />
        </label>
        <label htmlFor="feedback-period">
          <span>{t.feedback_period}</span>
          <select id="feedback-period" value={query.period} onChange={(e) => onQuery({ period: e.target.value as FeedbackQuery['period'], page: 1 })}>
            <option value="all">{t.feedback_period_all}</option>
            <option value="7">{t.feedback_period_7}</option>
            <option value="30">{t.feedback_period_30}</option>
            <option value="90">{t.feedback_period_90}</option>
          </select>
        </label>
        <label htmlFor="feedback-rating">
          <span>{t.feedback_rating_filter}</span>
          <select id="feedback-rating" value={query.rating ?? ''} onChange={(e) => onQuery({ rating: e.target.value ? Number(e.target.value) as FeedbackRating : null, page: 1 })}>
            <option value="">{t.feedback_rating_all}</option>
            {RATINGS.map((rating) => <option key={rating} value={rating}>{ratingLabel(lang, rating)}</option>)}
          </select>
        </label>
      </div>

      {error ? <div className="banner" role="alert">{errorText(lang, error)}</div> : null}
      {loading ? <p className="muted" role="status">{t.feedback_loading}</p> : null}

      {summary ? <>
        <div className="feedback-stats">
          {[
            { label: t.feedback_received, value: summary.received, icon: 'message' as const },
            { label: t.feedback_excellent_share, value: rated ? `${Math.round(summary.excellent / rated * 100)}%` : '—', icon: 'star' as const },
            { label: t.feedback_private_notes, value: summary.notes, icon: 'edit' as const },
            { label: t.feedback_unread, value: summary.unread, icon: 'mail' as const },
          ].map(({ label, value, icon }) => <div className="feedback-stat" key={label}>
            <Icon name={icon} /><span>{label}</span><strong><bdi dir="ltr">{value}</bdi></strong>
          </div>)}
        </div>
        {rated > 0 ? <section className="feedback-mix" aria-labelledby="feedback-mix-title">
          <div><h2 id="feedback-mix-title">{t.feedback_mix}</h2><p>{t.feedback_mix_hint}</p></div>
          <div className="feedback-distribution">
            {RATINGS.map((rating) => <div className="feedback-rating-row" key={rating}>
              <span>{ratingLabel(lang, rating)}</span>
              <div className="feedback-bar" aria-hidden="true"><span className={`rating-fill rating-${rating}`} style={{ width: `${counts[rating] / rated * 100}%` }} /></div>
              <bdi dir="ltr">{counts[rating]}</bdi>
            </div>)}
          </div>
        </section> : null}
      </> : null}

      <div className="feedback-statuses" role="group" aria-label={t.feedback_nav}>
        {statuses.map(({ value, label, count }) => <button key={value} type="button" className={query.status === value ? 'selected' : ''} aria-pressed={query.status === value} onClick={() => onQuery({ status: value, page: 1 })}>
          {label}{count !== undefined ? <bdi dir="ltr">{count}</bdi> : null}
        </button>)}
      </div>

      {data ? <>
        <div className="feedback-list-heading">
          <p role="status">{fill(t.feedback_results, { count: String(data.total) })}</p>
          {filtered ? <button className="btn secondary" type="button" onClick={onReset}>{t.feedback_reset}</button> : <span className="muted">{t.feedback_newest}</span>}
        </div>
        <div className="feedback-list" aria-busy={loading}>
          {data.items.length === 0 ? <div className="feedback-empty">
            <span className="feedback-empty-icon"><Icon name="message" /></span>
            <h2>{filtered ? t.feedback_empty_filter : t.feedback_empty_title}</h2>
            {!filtered ? <p className="muted">{t.feedback_empty_body}</p> : null}
          </div> : data.items.map((entry) => <article className={`feedback-entry ${entry.unread ? 'is-unread' : ''}`} key={entry.id}>
            <div className="feedback-entry-heading">
              <span className="feedback-order-icon"><Icon name="orders" /></span>
              <div>
                <h3><Filled template={t.feedback_order} vars={{ number: entry.display_no }} /></h3>
                <p className="muted"><bdi dir="auto">{entry.pickup_point}</bdi></p>
              </div>
              <time dateTime={new Date(entry.updated_at).toISOString()}><bdi dir="ltr">{date(entry.updated_at)}</bdi></time>
            </div>
            <div className="feedback-entry-tags">
              <span className={`tag feedback-rating rating-${entry.rating ?? 0}`}><Icon name={entry.rating === null ? 'message' : 'star'} />{entry.rating === null ? t.feedback_no_rating : ratingLabel(lang, entry.rating)}</span>
              {entry.answered_at === null ? <span className="tag"><Icon name="clock" />{t.feedback_pending}</span> : <span className={`tag ${entry.unread ? 'ready' : ''}`}><Icon name={entry.unread ? 'mail' : 'check'} />{entry.unread ? t.feedback_unread : t.feedback_read}</span>}
            </div>
            {entry.note ? <p className="feedback-note" dir="auto">{entry.note}</p> : <p className="muted feedback-note-empty">{entry.answered_at === null ? t.feedback_waiting : t.feedback_no_note}</p>}
            {entry.unread ? <button className="btn secondary feedback-read" type="button" onClick={() => onRead(entry)} disabled={reading !== null || loading}><Icon name="check" />{t.feedback_mark_read}</button> : null}
          </article>)}
        </div>
        {data.total > data.page_size || query.page > 1 ? <nav className="feedback-pagination" aria-label={t.feedback_nav}>
          <button className="btn secondary" type="button" disabled={query.page <= 1 || loading} onClick={() => onQuery({ page: query.page - 1 })}>{t.feedback_previous}</button>
          <span>{fill(t.feedback_page, { page: String(query.page), pages: String(pages) })}</span>
          <button className="btn secondary" type="button" disabled={query.page >= pages || loading} onClick={() => onQuery({ page: query.page + 1 })}>{t.feedback_next}</button>
        </nav> : null}
      </> : null}
      <p className="feedback-google-note muted"><Icon name="external" />{t.feedback_google_hint}</p>
    </section>
  );
}

/** Keyed by venue in the shell, so a switch cannot retain another venue's notes or request state. */
export function Feedback({ lang, venueId, reviewUrl }: { lang: Lang; venueId: string; reviewUrl: string | null }) {
  const [query, setQuery] = useState<FeedbackQuery>(DEFAULT_QUERY);
  const [data, setData] = useState<FeedbackPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const base = `/account/venues/${encodeURIComponent(venueId)}/feedback`;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setData(null);
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ status: query.status, period: query.period, page: String(query.page), q: query.search });
      if (query.rating !== null) params.set('rating', String(query.rating));
      api<FeedbackPage>(`${base}?${params}`, { signal: controller.signal }).then((result) => {
        if (controller.signal.aborted) return;
        const lastPage = Math.max(1, Math.ceil(result.total / result.page_size));
        if (query.page > lastPage) setQuery((q) => ({ ...q, page: lastPage }));
        else setData(result);
      }).catch((e: ApiError) => {
        if (controller.signal.aborted) return;
        if (e.status === 401) window.location.hash = '#/signin';
        else setError(e.code ?? 'internal');
      }).finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    }, query.search ? 250 : 0);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [base, query, refresh]);

  const read = async (entry: FeedbackEntry) => {
    setReading(entry.id);
    setError(null);
    try {
      await api(`${base}/${encodeURIComponent(entry.id)}/read`, { method: 'POST', body: JSON.stringify({ revision: entry.revision }) });
      setRefresh((n) => n + 1);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) window.location.hash = '#/signin';
      else setError(err.code ?? 'internal');
    } finally { setReading(null); }
  };

  return <FeedbackView lang={lang} reviewUrl={reviewUrl} data={data} query={query} loading={loading} error={error} reading={reading}
    onQuery={(patch) => setQuery((q) => ({ ...q, ...patch }))} onReset={() => setQuery(DEFAULT_QUERY)}
    onRefresh={() => setRefresh((n) => n + 1)} onRead={(entry) => void read(entry)} />;
}
